<payee-popup width="560px">
  <ng-template #payeePopupTitleTemplate>
    <h1 class="title">
      {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.TITLE" | translate }}
    </h1>
  </ng-template>

  <ng-template #payeePopupDescriptionTemplate>
    <div class="description">
      {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.DESCRIPTION" | translate }}
    </div>
  </ng-template>

  <form [formGroup]="form">
    @if (statementSummary(); as summary) {
      <div class="claim-card">
        <div class="claim-card-header">
          @if (summary.debtorName) {
            {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.CLAIM_TOWARDS" | translate: { name: summary.debtorName } }}
          } @else {
            {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.CLAIM_TOWARDS_UNKNOWN" | translate }}
          }
        </div>
        @for (total of summary.totals; track total.currency) {
          <div class="claim-row flex vcenter">
            <div class="claim-row-label">
              {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.PRINCIPAL" | translate }}
            </div>
            <div class="claim-row-value">
              {{ total.principal | currency: total.currency : "symbol-narrow" : "1.0" : "hu" }}
            </div>
          </div>
          @if (summary.hasAncillary) {
            <div class="claim-row flex vcenter">
              <div class="claim-row-label">
                {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.ANCILLARY" | translate }}
              </div>
              <div class="claim-row-value">
                {{ total.ancillary | currency: total.currency : "symbol-narrow" : "1.0" : "hu" }}
              </div>
            </div>
          }
        }
      </div>
    }

    <div class="payments">
      @for (payment of form.controls.payments.controls; track payment; let paymentIndex = $index) {
        @let currencyChoices = currencyOptions(paymentIndex);
        <div class="amount-section">
          <span class="label">
            {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.PAID_AMOUNT" | translate }}
          </span>
          <div class="amount-line">
            <div
              class="amount-combined"
              [class.error]="payment.controls.amount.dirty && payment.controls.amount.hasError('max')"
            >
              <input
                class="amount-input"
                placeholder="0"
                type="text"
                inputmode="numeric"
                [formControl]="payment.controls.amount"
              />
              <div
                #currencyTrigger="matMenuTrigger"
                class="currency-picker"
                [class.disabled]="currencyChoices.length <= 1"
                [matMenuTriggerFor]="currencyChoices.length > 1 ? currencyMenu : null"
              >
                <img
                  class="currency-flag"
                  [src]="payment.controls.currency.value | currencyFlagUrl"
                  [alt]="payment.controls.currency.value"
                />
                <span class="currency-code">{{ payment.controls.currency.value }}</span>
                @if (currencyChoices.length > 1) {
                  <mat-icon class="currency-arrow">expand_more</mat-icon>
                }
              </div>
              <mat-menu
                #currencyMenu
                xPosition="before"
              >
                <div class="currency-menu">
                  @for (currency of currencyChoices; track currency) {
                    <div
                      class="currency-dropdown-item"
                      role="button"
                      tabindex="0"
                      [class.selected]="currency === payment.controls.currency.value"
                      (click)="selectCurrency(payment, currency, currencyTrigger)"
                      (keydown.enter)="selectCurrency(payment, currency, currencyTrigger)"
                    >
                      <img
                        class="currency-flag"
                        [src]="currency | currencyFlagUrl"
                        [alt]="currency"
                      />
                      <span>{{ currency }}</span>
                    </div>
                  }
                </div>
              </mat-menu>
            </div>
            @if (multiCurrency() && form.controls.payments.length > 1) {
              <button
                type="button"
                payee-icon-button
                color="white"
                class="remove-payment"
                (click)="removePayment(paymentIndex)"
              >
                <payee-icon name="trash-red"></payee-icon>
              </button>
            }
          </div>
          @if (payment.controls.amount.dirty && payment.controls.amount.hasError("max")) {
            <span
              class="field-error"
              role="alert"
            >
              {{
                "WORKFLOW_DIALOGS.MANUAL_PAYMENT.MAX_AMOUNT"
                  | translate
                    : {
                        max: (payment.controls.amount.errors?.max?.max | number: "1.0" : "hu"),
                        currency: payment.controls.currency.value,
                      }
              }}
            </span>
          }
        </div>
      }

      @if (multiCurrency() && canAddPayment()) {
        <button
          type="button"
          class="add-payment"
          (click)="addPayment()"
        >
          <payee-icon
            name="plus-blue"
            width="16px"
            height="16px"
          ></payee-icon>
          <span>{{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.ADD_CURRENCY" | translate }}</span>
        </button>
      }
    </div>

    @if (askForPaymentDate() && hasPositivePayment()) {
      <div class="due-date-section">
        <span class="label">
          {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.PAID_DATE" | translate }}
        </span>
        <div
          class="date-combined"
          [class.error]="showDateError(form.controls.paid_at)"
        >
          <input
            class="date-input"
            type="text"
            [formControl]="form.controls.paid_at"
            [matDatepicker]="paidAtPicker"
            [max]="maxPaymentDate"
            (focus)="paidAtPicker.open(); revealDate(form.controls.paid_at)"
          />
          <button
            type="button"
            class="date-picker-trigger"
            (click)="paidAtPicker.open(); revealDate(form.controls.paid_at)"
          >
            <payee-icon
              name="calendar"
              width="24"
              height="24"
            ></payee-icon>
          </button>
          <mat-datepicker #paidAtPicker></mat-datepicker>
        </div>
        @if (showDateError(form.controls.paid_at)) {
          <span
            class="field-error"
            role="alert"
          >
            @if (form.controls.paid_at.hasError("required")) {
              {{ "ERROR_MESSAGES.FIELD_REQUIRED" | translate }}
            }
            @if (form.controls.paid_at.hasError("maxdate")) {
              {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.PAID_DATE_MAX" | translate }}
            }
          </span>
        }
      </div>
    }

    @if (optionalClaimsLocked() && hasOptionalClaims() && paymentCoversPrincipal()) {
      <payee-info class="optional-claims-info">
        {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.OPTIONAL_CLAIMS_NOT_REDEEMABLE" | translate }}
      </payee-info>
    }
  </form>

  <ng-template #payeePopupSubmitTemplate>
    <button
      class="payee-button"
      payee-button
      color="blue"
      width="max"
      height="normal"
      [disabled]="loading()"
      [loading]="loading()"
      (click)="submit()"
    >
      <ng-container>
        {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.SUBMIT" | translate }}
      </ng-container>
    </button>
  </ng-template>

  <ng-template #payeePopupCancelTemplate>
    <button
      class="payee-button"
      payee-button
      color="white"
      width="max"
      height="normal"
      [isBorderButton]="true"
      [mat-dialog-close]="false"
    >
      <ng-container>
        {{ "WORKFLOW_DIALOGS.HARD_PERFORMANCE_CLAIM_RESPONSE.CANCEL" | translate }}
      </ng-container>
    </button>
  </ng-template>
</payee-popup>
